import { useDispatch, useSelector } from "react-redux";
import {
  getUserData,
  getUserDataTimeZone,
  setExamHistory,
} from "../../store/login.slice";
import {
  useGettestsQuery,
  useStartIncompleteTestMutation,
} from "../../api/testSlice.ts/testSlice";
import { useNavigate } from "react-router-dom";
import useTokenSpoilCheck from "../../hooks/tokenspoilcheck";
import { useEffect, useState } from "react";
import { setCreatedTest, setTestStartSeconds } from "../../store/test.slice";
import { formatDateTime } from "../../helpers/formatters";
import { toast } from "react-toastify";
import { CheckSquare } from "lucide-react";
import styles from "./testLists.module.scss";

const clearedSorting = {
  start: "",
  last: "",
  questions: "",
};

const sortIndicator = (mode) =>
  mode === "ASC" ? " ▲" : mode === "DESC" ? " ▼" : "";

const nextSortMode = (mode) =>
  mode === "" ? "ASC" : mode === "ASC" ? "DESC" : "";

function IncompleteTests() {
  //
  //Selectors
  //
  const userData = useSelector(getUserData);
  const timeZone = useSelector(getUserDataTimeZone);

  //
  //local state
  //
  const [expanded, setExpanded] = useState([]);
  const [sortingMode, setSortingMode] = useState({
    ...clearedSorting,
    last: "DESC",
  });
  const [selectedExam, setSelectedExam] = useState(undefined);
  const [searchTerm, setSearchTerm] = useState("");
  const [errorStatusCode, setErrorStatusCode] = useState(undefined);

  //
  //Hooks
  //
  useTokenSpoilCheck(errorStatusCode);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  //
  //Queries
  //
  const [startIncompleteTest] = useStartIncompleteTestMutation();

  const { data: tests, refetch } = useGettestsQuery({});

  //
  //UseEffect hooks
  //
  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (tests?.CompletedTests && tests?.IncompleteTests) {
      const CompletedTests = tests.CompletedTests;
      const IncompleteTests = tests.IncompleteTests;
      dispatch(setExamHistory({ CompletedTests, IncompleteTests }));
    }
  }, [tests]);

  // Pre-select the most recently used test
  useEffect(() => {
    if (tests?.IncompleteTests?.length > 0) {
      const mostRecentTest = tests.IncompleteTests.reduce((latest, test) =>
        new Date(test.LastUsed) > new Date(latest.LastUsed) ? test : latest,
      );
      setSelectedExam(mostRecentTest);
    }
  }, [tests]);

  //
  //Sorting / filtering
  //
  const Data = [...(userData.IncompleteTests || [])];

  const sortedData = Data.sort((a, b) => {
    if (sortingMode.start) {
      const startA = new Date(a.TimeTestStarted).getTime();
      const startB = new Date(b.TimeTestStarted).getTime();
      return sortingMode.start === "ASC" ? startA - startB : startB - startA;
    }

    if (sortingMode.last) {
      const lastA = new Date(a.LastUsed).getTime();
      const lastB = new Date(b.LastUsed).getTime();
      return sortingMode.last === "ASC" ? lastA - lastB : lastB - lastA;
    }

    if (sortingMode.questions) {
      const questionsA = a?.questions_for_test?.length ?? 0;
      const questionsB = b?.questions_for_test?.length ?? 0;
      return sortingMode.questions === "ASC"
        ? questionsA - questionsB
        : questionsB - questionsA;
    }
    return 0;
  });

  const visibleData = sortedData.filter((test) => {
    const term = searchTerm.toLowerCase();
    return (
      (test?.TestName || "").toLowerCase().includes(term) ||
      (test?.guid || "").toLowerCase().includes(term)
    );
  });

  const cycleSort = (key) => {
    setSortingMode({ ...clearedSorting, [key]: nextSortMode(sortingMode[key]) });
  };

  //
  //Start test
  //
  const startTest = async (exam) => {
    if (exam == undefined) {
      toast("Please select a test");
      return;
    }
    try {
      const result = await startIncompleteTest({
        testStartData: {
          guid: exam.guid,
        },
      }).unwrap();
      dispatch(
        setTestStartSeconds({
          testStartSeconds: result.time_left,
          testStartDate: Date.now(),
        }),
      );
      dispatch(
        setCreatedTest({
          createdTest: {
            QuestionsForTest: result.questions_for_test,
            guid: exam.guid,
            CapturedData: result.user_captured_data,
            tutorMode: result.tutor_mode,
          },
        }),
      );
      navigate("/test");
    } catch (error) {
      console.error("Error occurred during test creation:", error);
      setErrorStatusCode(error?.status);
      return;
    }
  };

  return (
    <div className={styles.pageContainer}>
      <div className={styles.headerContainer}>
        <div className={styles.titleContainer}>
          <CheckSquare />
          <h1 className={styles.titleText}>Incomplete Tests</h1>
        </div>

        <div className={styles.filterContainer}>
          <p className={styles.countText}>
            {visibleData.length} test{visibleData.length == 1 ? "" : "s"}{" "}
            suspended
          </p>
          <div className={styles.controls}>
            <input
              className={styles.searchInput}
              placeholder="Search by test name or ID"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button
              className={styles.primaryButton}
              onClick={() => cycleSort("start")}
            >
              Sort By Start Date {sortingMode.start}
            </button>
            <button
              className={styles.primaryButton}
              onClick={() => cycleSort("last")}
            >
              Sort By Last Used {sortingMode.last}
            </button>
            <button
              className={styles.primaryButton}
              onClick={() => cycleSort("questions")}
            >
              Sort By Questions {sortingMode.questions}
            </button>
          </div>
        </div>

        {visibleData.length > 0 && (
          <div className={styles.tableWrapper}>
            <div className={styles.tableScroll}>
              <table className={`${styles.table} ${styles.incompleteTable}`}>
                <thead>
                  <tr>
                    <th>Test</th>
                    <th
                      className={styles.sortableHeader}
                      onClick={() => cycleSort("start")}
                    >
                      Started{sortIndicator(sortingMode.start)}
                    </th>
                    <th
                      className={styles.sortableHeader}
                      onClick={() => cycleSort("questions")}
                    >
                      Questions{sortIndicator(sortingMode.questions)}
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleData.map((test) => {
                    const started = new Date(test.TimeTestStarted);
                    return (
                      <tr
                        key={test.TimeTestStarted}
                        className={
                          selectedExam?.guid == test.guid
                            ? styles.selectedOption
                            : ""
                        }
                        onClick={() => setSelectedExam(test)}
                      >
                        {test.TestName ? (
                          <td title={test.guid}>{test.TestName}</td>
                        ) : (
                          <td className={styles.testIdCell}>
                            <button
                              className={styles.secondaryButton}
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpanded((prev) =>
                                  prev.includes(test.guid)
                                    ? prev.filter((id) => id !== test.guid)
                                    : [...prev, test.guid],
                                );
                              }}
                            >
                              {expanded.includes(test.guid)
                                ? test?.guid || "Unknown"
                                : test?.guid?.slice(0, 7) || "Unknown"}
                            </button>
                          </td>
                        )}
                        <td>{formatDateTime(timeZone?.value, started)}</td>
                        <td>{test?.questions_for_test?.length ?? "Unknown"}</td>
                        <td
                          className={`${styles.tableActionCell} ${styles.tableActionFinal}`}
                        >
                          <button
                            className={styles.reviewRowButton}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedExam(test);
                              startTest(test);
                            }}
                          >
                            Continue
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {sortedData.length == 0 && (
          <div className={styles.warning}>
            Your suspended/incomplete tests will appear here if you suspend a
            test without completing it
          </div>
        )}
        {sortedData.length > 0 && visibleData.length == 0 && (
          <div className={styles.warning}>No tests match your search.</div>
        )}
      </div>
      <div className={styles.footerContainer}>
        <button
          className={styles.primaryButton}
          onClick={() => startTest(selectedExam)}
        >
          <span>Continue Test</span>
        </button>
      </div>
    </div>
  );
}

export default IncompleteTests;